import {
  appAuthAtom,
  AuthStage,
  AuthState,
  IdTokenPayload,
  isDefined,
  Keys,
  LogoutReason,
  SessionResource,
} from '@eih-netbroker/common';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AccountContext } from '../../../context/AccountContext';
import { useDialog } from '../../../context/DialogContext';
import { useLocale } from '../../../context/NbLocalizationContext';
import { useSnackbar } from '../../../context/SnackbarContext';
import { Config } from '../../config/config';
import { routes } from '../../data/routes';
import { primaryInstanceAtom } from '../../state/primaryInstanceAtom';
import { getJWTPayload } from '../../utils/jwtUtils';
import { useCookie } from '../../utils/useCookie';
import { useLogger } from '../../utils/useLogger';
import { useResource, useResourceHeaders } from '../useResource';

/**
 * List of logout reasons that should not trigger SSO logout
 *  - when token is expired, no need to redirect the user to sso logout page
 *  - inactivity is not a reason to redirect the user to sso logout page
 *  - app initialization failed
 *  - consent is rejected
 */
const reasonsToSkipSSOLogout = [
  LogoutReason.TokenExpired,
  LogoutReason.SignInFailedToInitializeApp,
  LogoutReason.ErrorWhileSavingConsent,
  LogoutReason.UserDisabled,
];

export const useAuthEvents = () => {
  const { translation } = useLocale();
  const logger = useLogger('useAuthEvents');
  const sessionService = useResource(SessionResource);
  const accountContext = useContext(AccountContext);
  const { getResourceHeaderWithToken } = useResourceHeaders();
  const [, setAppAuth] = useAtom(appAuthAtom);
  const navigate = useNavigate();
  const { setCookie, eraseCookie } = useCookie();
  const { showError, closeAllSnackbars } = useSnackbar();
  const { closeDialogs } = useDialog();
  const isPrimaryInstanceAtom = useAtomValue(primaryInstanceAtom);

  if (!accountContext) {
    throw new Error('AccountContext is not defined, cannot handle auth events');
  }
  const { closeAccountPopover } = accountContext;

  const otherInstanceLoginInProgress = useCallback(() => {
    setAppAuth({
      isAuthenticated: true,
      authStatus: AuthState.Loading,
      stage: AuthStage.OtherInstanceLoginInProgress,
      secrets: { JwtToken: '', idToken: '', IdPayload: { sub: '', userGUID: '', clientId: '', exp: 0, auth_time: 0 } },
    });
  }, [setAppAuth]);

  const verifyAndStoreToken = useCallback(
    async (
      jwtToken: string,
      idToken: string,
      props?: { fromOtherInstance?: boolean; refreshToken?: boolean },
    ): Promise<void> => {
      const IdPayload = getJWTPayload<IdTokenPayload>(idToken);
      if (!isDefined(IdPayload.userGUID)) {
        throw Error('User id is not defined in JWT token');
      }

      setCookie(Keys.Cookie.JWT, `Bearer ${jwtToken}`, 1, Config.App.Domain, true);
      if (props?.fromOtherInstance) {
        setAppAuth({
          isAuthenticated: true,
          authStatus: AuthState.Loading,
          stage: AuthStage.Step1_LoadUserSettings,
          secrets: {
            JwtToken: jwtToken,
            idToken,
            IdPayload,
          },
        });
      } else {
        if (!props?.refreshToken) {
          setAppAuth({
            isAuthenticated: true,
            authStatus: AuthState.Loading,
            stage: AuthStage.Step1_LoadUserSettings,
            secrets: {
              JwtToken: jwtToken,
              idToken,
              IdPayload,
            },
          });
        } else {
          setAppAuth(curr => ({
            ...curr,
            secrets: {
              JwtToken: jwtToken,
              idToken,
              IdPayload,
            },
          }));
          try {
            await sessionService.updateSessionToken(getResourceHeaderWithToken(), jwtToken);
          } catch (err) {
            showError(translation.general.error.tokenUpdateError, { persist: true });
          }
        }
      }
    },
    [setAppAuth, setCookie, getResourceHeaderWithToken, sessionService, showError, translation],
  );

  /**
   * Logout method
   * DO NOT CALL THIS DIRECTLY, use logoutInternal from useAuthorizationFlow.tsx
   */
  const handleSignOff = useCallback(
    async (ssoLogout: (() => Promise<void>) | undefined, reason: LogoutReason | undefined | null): Promise<void> => {
      // We must log out from the primary instance, otherwise SSO will confirm the logout
      if (!isPrimaryInstanceAtom) {
        // if this instance is not a primary one, we don't have to do anything, because the primary instance will handle the logout
        // and then send the logout event to all other instances, which is handled in SSE
        logger.info(`Logging out because of ${reason ?? 'other instance logged out'}, nothing to do here`);
        setAppAuth({ isAuthenticated: false, authStatus: AuthState.NotAuthenticated });
        navigate(routes.login);
        return;
      } else if (!ssoLogout) {
        // on another instance initiated the logout, but we don't have the sso logout method here.
        // so let's start the signoff process
        setAppAuth({ isAuthenticated: false, authStatus: AuthState.SignOff, error: reason || undefined });
        return;
      }

      logger.info(`Logging out because of ${reason ?? 'other instance logged out'}`);
      eraseCookie(Keys.Cookie.JWT);

      try {
        await sessionService.deleteSession(getResourceHeaderWithToken());
      } catch (err) {
        logger.error('Failed to delete session', err);
      }

      // if there is a reason to logout from SSO, do it
      if (ssoLogout && (!reason || !reasonsToSkipSSOLogout.includes(reason))) {
        try {
          await ssoLogout();
          logger.info(`SSO Logout`);
        } catch (err) {
          logger.error(`Failed to logout from SSO`, err);
        }
      }

      // make sure all snackbars are closed
      closeAllSnackbars();
      // make sure that accounts popover is closed
      closeAccountPopover();
      // make sure to close all dialogs
      closeDialogs();
      // set app auth state to unauthorized, so we won't try to load the app
      setAppAuth({ isAuthenticated: false, authStatus: AuthState.NotAuthenticated, error: reason || undefined });

      // clean atoms
      const logoutEvent = new Event('logout');
      document.dispatchEvent(logoutEvent);

      // otherwise redirect to login page (practically when there is ssoLogout we are redirecting the user, so navigate won't be called)
      navigate(routes.login);
      // window.location.reload();
    },
    [
      setAppAuth,
      navigate,
      sessionService,
      getResourceHeaderWithToken,
      eraseCookie,
      logger,
      closeAllSnackbars,
      closeAccountPopover,
      closeDialogs,
      isPrimaryInstanceAtom,
    ],
  );

  return useMemo(
    () => ({
      verifyAndStoreToken,
      handleSignOff,
      otherInstanceLoginInProgress,
    }),
    [verifyAndStoreToken, handleSignOff, otherInstanceLoginInProgress],
  );
};
